import { useState, useCallback } from 'react';
import { DateRangeType, getDateRange, normalizeDateRange } from '../utils/dateRangeUtils';

interface UseDateRangeReturn {
  rangeType: DateRangeType;
  startDate: Date | null;
  endDate: Date | null;
  setRangeType: (type: DateRangeType) => void;
  handleStartDateChange: (date: Date | null) => void;
  handleEndDateChange: (date: Date | null) => void;
  handleRangeChange: (type: DateRangeType, onAutoFetch?: (start: Date, end: Date) => void) => void;
}

/**
 * Custom hook for managing date range state and validation
 * Centralizes all date range logic
 * NO timezone shifting in state - dates are stored as-is
 */
export function useDateRange(initialType: DateRangeType = 'custom'): UseDateRangeReturn {
  const [rangeType, setRangeType] = useState<DateRangeType>(initialType);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Handle start date change with validation
  // NO timezone conversion - store Date as-is
  const handleStartDateChange = useCallback((date: Date | null) => {
    const normalized = normalizeDateRange(date, endDate);
    setStartDate(normalized.start);
    setEndDate(normalized.end);
  }, [endDate]);

  // Handle end date change with validation
  // NO timezone conversion - store Date as-is
  const handleEndDateChange = useCallback((date: Date | null) => {
    const normalized = normalizeDateRange(startDate, date);
    setStartDate(normalized.start);
    setEndDate(normalized.end);
  }, [startDate]);

  // Handle range type change with auto-calculation
  const handleRangeChange = useCallback((
    type: DateRangeType,
    onAutoFetch?: (start: Date, end: Date) => void
  ) => {
    setRangeType(type);
    
    // Auto-calculate and fetch for preset ranges
    if (type !== 'custom') {
      const range = getDateRange(type);
      if (range) {
        setStartDate(range.start);
        setEndDate(range.end);
        
        // Trigger auto-fetch if callback provided
        if (onAutoFetch) {
          onAutoFetch(range.start, range.end);
        }
      }
    }
  }, []);

  return {
    rangeType,
    startDate,
    endDate,
    setRangeType,
    handleStartDateChange,
    handleEndDateChange,
    handleRangeChange
  };
}
